/**
 * 파편 풀 관리 순수 로직 (data-model §3, FR-004b/c).
 * 전역 상한(활성 파편)·수명·최고령 축출 규칙만 다룬다. 실제 rigid body/렌더는
 * DebrisPool이 이 결정을 따라 수행한다. three/rapier 비의존(테스트 가능).
 */
import type { DebrisPhase } from '../core/types';
import { BALANCE, type BalanceConfig } from '../config/balance';

interface Entry {
  id: number;
  spawnTime: number;
  phase: DebrisPhase; // 'active' | 'fading' ('free'는 맵에서 제거)
}

export interface EvictInfo {
  id: number;
  reason: 'lifetime' | 'cap';
}

export class DebrisPoolLogic {
  private entries = new Map<number, Entry>();
  private nextId = 1;

  constructor(private cfg: BalanceConfig = BALANCE) {}

  /** 현재 'active' 파편 수 (상한 대상). */
  get activeCount(): number {
    let n = 0;
    for (const e of this.entries.values()) if (e.phase === 'active') n++;
    return n;
  }

  /**
   * 파편 1개 스폰 결정. 상한 초과 시 최고령 active를 fading으로 축출.
   * @returns 새 파편 id와 축출 목록(reason 'cap').
   */
  spawn(now: number): { id: number; evicted: EvictInfo[] } {
    const evicted: EvictInfo[] = [];
    if (this.activeCount >= this.cfg.debris.globalCap) {
      const oldest = this.oldestActive();
      if (oldest) {
        oldest.phase = 'fading';
        evicted.push({ id: oldest.id, reason: 'cap' });
      }
    }
    const id = this.nextId++;
    this.entries.set(id, { id, spawnTime: now, phase: 'active' });
    return { id, evicted };
  }

  /** 수명(8초) 만료된 active를 fading으로 전환(순수). */
  update(now: number): EvictInfo[] {
    const expired: EvictInfo[] = [];
    for (const e of this.entries.values()) {
      if (e.phase === 'active' && now - e.spawnTime >= this.cfg.debris.lifetimeSec) {
        e.phase = 'fading';
        expired.push({ id: e.id, reason: 'lifetime' });
      }
    }
    return expired;
  }

  /** 페이드 완료 → 풀 반환(엔트리 제거). */
  free(id: number): void {
    this.entries.delete(id);
  }

  /** 전체 초기화(리셋). */
  clear(): void {
    this.entries.clear();
  }

  private oldestActive(): Entry | null {
    let oldest: Entry | null = null;
    for (const e of this.entries.values()) {
      if (e.phase !== 'active') continue;
      if (!oldest || e.spawnTime < oldest.spawnTime) oldest = e;
    }
    return oldest;
  }
}
